import React, { useState, useEffect, useRef } from 'react';
import { settingsService } from '../../services/settingsService';

// Searchable Select Component
function SearchableSelect({ value, onChange, options, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  
  const selectedOption = options.find(opt => opt.value === value);
  
  const filteredOptions = search 
    ? options.filter(opt => 
        opt.label.toLowerCase().includes(search.toLowerCase()) ||
        opt.value.toLowerCase().includes(search.toLowerCase())
      )
    : options;
  
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
  };
  
  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div 
        className="form-control d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </div>
      {isOpen && (
        <div style={{ 
          position: 'absolute', 
          top: '100%', 
          left: 0, 
          right: 0, 
          zIndex: 1000,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          maxHeight: '250px',
          overflowY: 'auto'
        }}>
          <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
            <input 
              type="text" 
              className="form-control form-control-sm"
              placeholder="Cari kode atau nama akun..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '10px', color: '#999', textAlign: 'center' }}>Tidak ditemukan</div>
            ) : (
              filteredOptions.map(opt => (
                <div 
                  key={opt.value}
                  style={{ 
                    padding: '10px', 
                    cursor: 'pointer',
                    background: opt.value === value ? '#e3f2fd' : 'white',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onClick={() => handleSelect(opt.value)}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = opt.value === value ? '#e3f2fd' : 'white'}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Config() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coaList, setCoaList] = useState([]);
  const [config, setConfig] = useState({
    simpanan: {},
    akun: {},
    bunga: {},
    denda: {},
    pendapatan: {},
    umum: {}
  });

  useEffect(() => {
    fetchConfig();
    fetchCOAForDropdown();
  }, []);

  const fetchCOAForDropdown = async () => {
    try {
      const res = await settingsService.getCOA();
      let data = res.data.data;
      if (data && !Array.isArray(data) && Array.isArray(data.data)) {
        data = data.data;
      }
      setCoaList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching COA for dropdown:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await settingsService.getSystemConfig();
      console.log('Config API full response:', res);
      console.log('Config API res.data:', res.data);
      // Handle nested data: res.data.data.data
      let responseData = res.data.data || {};
      let data = responseData.data || { simpanan: {}, akun: {}, bunga: {}, denda: {}, umum: {} };
      console.log('Config extracted data:', data);
      console.log('Config.simpanan:', data.simpanan);
      console.log('Config.akun:', data.akun);
      console.log('Config.bunga:', data.bunga);
      console.log('Config.pendapatan:', data.pendapatan);
      console.log('Config keys:', Object.keys(data));

      // Fetch SHU settings for current year
      const currentYear = new Date().getFullYear();
      console.log('Fetching SHU settings for year:', currentYear);
      try {
        const shuRes = await settingsService.getSHUSettings(currentYear);
        console.log('SHU API response:', shuRes);
        console.log('SHU API res.data:', shuRes.data);
        console.log('SHU API res.data.data:', shuRes.data.data);
        if (shuRes.data.data) {
          // Convert string "1"/"0" to boolean
          const shuValue = shuRes.data.data.SimpananSukarelaSHU;
          const booleanValue = shuValue === true || shuValue === '1' || shuValue === 1;
          data.shu = {
            simpananSukarelaSHU: {
              value: booleanValue
            }
          };
          console.log('Set data.shu:', data.shu);
          console.log('SimpananSukarelaSHU raw value:', shuValue, '-> boolean:', booleanValue);
        }
      } catch (shuError) {
        console.error('Error fetching SHU settings:', shuError);
        // If SHU settings don't exist for this year, use default
        data.shu = {
          simpananSukarelaSHU: {
            value: true
          }
        };
      }

      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const updates = {};
      Object.entries(config.simpanan || {}).forEach(([k, v]) => updates[k] = v.value);
      Object.entries(config.akun || {}).forEach(([k, v]) => updates[k] = v.value);
      Object.entries(config.bunga || {}).forEach(([k, v]) => updates[k] = v.value);
      Object.entries(config.denda || {}).forEach(([k, v]) => updates[k] = v.value);
      Object.entries(config.pendapatan || {}).forEach(([k, v]) => updates[k] = v.value);
      Object.entries(config.pinjaman || {}).forEach(([k, v]) => updates[k] = v.value);
      Object.entries(config.shu || {}).forEach(([k, v]) => updates[k] = v.value);

      await settingsService.updateSystemConfig(updates);

      // Handle SHU settings separately
      if (config.shu && config.shu.simpananSukarelaSHU) {
        const currentYear = new Date().getFullYear();
        await settingsService.updateSHUSettings(currentYear, {
          SimpananSukarelaSHU: config.shu.simpananSukarelaSHU.value
        });
      }

      alert('Pengaturan berhasil disimpan');
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const updateConfigValue = (section, key, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: { ...(prev[section]?.[key] || {}), value }
      }
    }));
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val || 0);
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="card-title"><i className="fas fa-cog mr-2"></i>Setting Dasar</h3>
        <button className="btn btn-primary" onClick={saveConfig} disabled={saving}>
          {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-save mr-2"></i>}
          Simpan Semua
        </button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-3"><i className="fas fa-spinner fa-spin mr-2"></i>Memuat...</div>
        ) : (
          <>
            <div className="card mb-3 border-primary">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0"><i className="fas fa-piggy-bank mr-2"></i>Simpanan</h5>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>Catatan:</strong> Simpanan Pokok dan Simpanan Wajib <strong>tidak mendapat bunga bulanan</strong>. 
                  Anggota mendapat imbalan berupa <strong>SHU (Sisa Hasil Usaha)</strong> di akhir tahun buku. 
                  Hanya Simpanan Sukarela yang mendapat bunga.
                </div>
                <div className="row">
                  {/* Simpanan Pokok Default */}
                  <div className="col-md-6 mb-3">
                    <label>Simpanan Pokok Default (Rp)</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={config.simpanan?.simpanan_pokok_default?.value || '100000'} 
                      onChange={e => updateConfigValue('simpanan', 'simpanan_pokok_default', e.target.value)}
                    />
                    <small className="text-muted d-block">Total yang harus dibayar (bisa dicicil). Tidak dapat ditarik selama jadi anggota. Dapat SHU di akhir tahun.</small>
                  </div>
                  
                  {/* Minimal Simpanan Pokok saat Pendaftaran */}
                  <div className="col-md-6 mb-3">
                    <label>Minimal Simpanan Pokok saat Pendaftaran (Rp)</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={config.simpanan?.simpanan_pokok_min_registrasi?.value || '25000'} 
                      onChange={e => updateConfigValue('simpanan', 'simpanan_pokok_min_registrasi', e.target.value)}
                    />
                    <small className="text-muted d-block">Minimal wajib dibayar saat daftar. Kekurangan dibayar bulanan.</small>
                  </div>
                  
                  {/* Simpanan Wajib per Bulan */}
                  <div className="col-md-6 mb-3">
                    <label>Simpanan Wajib per Bulan (Rp)</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={config.simpanan?.simpanan_wajib_default?.value || '50000'} 
                      onChange={e => updateConfigValue('simpanan', 'simpanan_wajib_default', e.target.value)}
                    />
                    <small className="text-muted d-block">Fixed amount per bulan, tidak ada minimum. Lewat bulan = tidak bayar = tidak ikut RAT. Dapat SHU di akhir tahun.</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3 border-info">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0"><i className="fas fa-percentage mr-2"></i>Bunga & Denda</h5>
              </div>
              <div className="card-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  <strong>Bunga Simpanan</strong> hanya berlaku untuk <strong>Simpanan Sukarela</strong>. 
                  Simpanan Pokok dan Wajib tidak mendapat bunga bulanan (mendapat SHU di akhir tahun).
                </div>

                {/* Simpanan Sukarela SHU Setting */}
                <div className="form-group mb-4">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="simpananSukarelaSHU"
                      checked={config.shu?.simpananSukarelaSHU?.value === true}
                      onChange={e => updateConfigValue('shu', 'simpananSukarelaSHU', e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="simpananSukarelaSHU">
                      <strong>Simpanan Sukarela Termasuk Perhitungan SHU</strong>
                    </label>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Current value: {config.shu?.simpananSukarelaSHU?.value !== undefined ? String(config.shu.simpananSukarelaSHU.value) : 'undefined'}
                  </small>
                  <small className="text-muted d-block mt-1">
                    Jika dicentang, simpanan sukarela tidak mendapat bunga bulanan tetapi mendapat SHU di akhir tahun.
                    Jika tidak dicentang, simpanan sukarela mendapat bunga bulanan tetapi tidak termasuk perhitungan SHU.
                  </small>
                </div>

                {/* Bunga Settings */}
                <h6 className="text-info mb-3"><strong>Bunga Simpanan</strong></h6>
                {Object.keys(config.bunga || {}).length === 0 ? (
                  <div className="alert alert-light text-muted mb-4">Belum ada pengaturan bunga.</div>
                ) : (
                <div className="row mb-4">
                  {Object.entries(config.bunga || {}).map(([key, val]) => (
                    <div className="col-md-4 mb-3" key={key}>
                      <label>{val?.desc || key}</label>
                      <input type="text" className="form-control" value={val?.value || ''} onChange={e => updateConfigValue('bunga', key, e.target.value)} />
                    </div>
                  ))}
                </div>
                )}
                
                <hr className="my-4" />
                
                {/* Denda Settings */}
                <h6 className="text-danger mb-3"><strong>Denda Keterlambatan</strong></h6>
                {Object.keys(config.denda || {}).length === 0 ? (
                  <div className="alert alert-light text-muted">Belum ada pengaturan denda.</div>
                ) : (
                <div className="row">
                  {/* Denda % per hari */}
                  <div className="col-md-4 mb-3">
                    <label><strong>Denda per Hari (%)</strong></label>
                    <input 
                      type="number" 
                      className="form-control mb-2" 
                      value={config.denda?.denda_keterlambatan_per_hari?.value || ''} 
                      onChange={e => updateConfigValue('denda', 'denda_keterlambatan_per_hari', e.target.value)}
                    />
                    
                  </div>
                  
                  {/* Toleransi hari */}
                  <div className="col-md-4 mb-3">
                    <label><strong>Toleransi Keterlambatan (hari)</strong></label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={config.denda?.toleransi_keterlambatan_hari?.value || ''} 
                      onChange={e => updateConfigValue('denda', 'toleransi_keterlambatan_hari', e.target.value)}
                    />
                  </div>
                  
                  {/* Denda aktif/nonaktif */}
                  <div className="col-md-4 mb-3">
                    <label><strong>Status Denda</strong></label>
                    <select 
                      className="form-control"
                      value={config.denda?.denda_keterlambatan_aktif?.value || '1'}
                      onChange={e => updateConfigValue('denda', 'denda_keterlambatan_aktif', e.target.value)}
                    >
                      <option value="1">Aktif</option>
                      <option value="0">Nonaktif</option>
                    </select>
                  </div>
                </div>
                )}
              </div>
            </div>

            <div className="card mb-3 border-info">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0"><i className="fas fa-hand-holding-usd mr-2"></i>Pengaturan Pinjaman</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label><strong>Bunga Prorata (Per Hari)</strong></label>
                    <select 
                      className="form-control"
                      value={config.pinjaman?.bunga_prorata_aktif?.value || '0'}
                      onChange={e => updateConfigValue('pinjaman', 'bunga_prorata_aktif', e.target.value)}
                    >
                      <option value="1">Aktif - Bunga dihitung per hari untuk pinjaman pertengahan bulan</option>
                      <option value="0">Nonaktif - Bunga bulan penuh meski pinjaman pertengahan bulan</option>
                    </select>
                    <small className="text-muted">
                      Jika aktif: Pinjam tanggal 15 April = bunga 15/30 bulan. Jika nonaktif: bunga penuh 1 bulan.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3 border-warning">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0"><i className="fas fa-chart-pie mr-2"></i>Pengaturan SHU (Sisa Hasil Usaha)</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label><strong>Bulan Mulai Periode SHU</strong></label>
                    <select 
                      className="form-control"
                      value={config.shu?.shu_bulan_mulai?.value || '1'}
                      onChange={e => updateConfigValue('shu', 'shu_bulan_mulai', e.target.value)}
                    >
                      <option value="1">Januari</option>
                      <option value="2">Februari</option>
                      <option value="3">Maret</option>
                      <option value="4">April</option>
                      <option value="5">Mei</option>
                      <option value="6">Juni</option>
                      <option value="7">Juli</option>
                      <option value="8">Agustus</option>
                      <option value="9">September</option>
                      <option value="10">Oktober</option>
                      <option value="11">November</option>
                      <option value="12">Desember</option>
                    </select>
                    <small className="text-muted">
                      Bulan awal periode perhitungan SHU (contoh: Januari untuk periode Jan-Des)
                    </small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label><strong>Bulan Akhir Periode SHU</strong></label>
                    <select 
                      className="form-control"
                      value={config.shu?.shu_bulan_akhir?.value || '12'}
                      onChange={e => updateConfigValue('shu', 'shu_bulan_akhir', e.target.value)}
                    >
                      <option value="1">Januari</option>
                      <option value="2">Februari</option>
                      <option value="3">Maret</option>
                      <option value="4">April</option>
                      <option value="5">Mei</option>
                      <option value="6">Juni</option>
                      <option value="7">Juli</option>
                      <option value="8">Agustus</option>
                      <option value="9">September</option>
                      <option value="10">Oktober</option>
                      <option value="11">November</option>
                      <option value="12">Desember</option>
                    </select>
                    <small className="text-muted">
                      Bulan akhir periode perhitungan SHU (contoh: Desember untuk periode Jan-Des)
                    </small>
                  </div>
                </div>
                <div className="alert alert-info">
                  <small>
                    <strong>Penjelasan Perhitungan:</strong><br/>
                    SHU dihitung berdasarkan <strong>lama mengendapnya saham</strong> setiap anggota dalam periode tahunan. 
                    Contoh: Jika sukarela Rp 1.000.000 ditarik tanggal 1 April dan dimasukkan lagi tanggal 27 April, 
                    maka lama mengendap hanya 3 hari (27-30 April), bukan 1 bulan penuh.
                  </small>
                </div>
              </div>
            </div>

            <div className="card mb-3 border-success">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0"><i className="fas fa-money-bill-wave mr-2"></i>Pendapatan Pendaftaran Anggota Baru</h5>
              </div>
              <div className="card-body">
                {Object.keys(config.pendapatan || {}).length === 0 ? (
                  <div className="alert alert-light text-muted">Belum ada pengaturan pendapatan.</div>
                ) : (
                  <div className="row">
                    {/* Uang Buku */}
                    <div className="col-md-6 mb-3">
                      <label><strong>Uang Buku (Rp)</strong></label>
                      <input 
                        type="number" 
                        className="form-control mb-2" 
                        value={config.pendapatan?.pendapatan_uang_buku?.value || ''} 
                        onChange={e => updateConfigValue('pendapatan', 'pendapatan_uang_buku', e.target.value)}
                      />
                      <small className="text-muted d-block mb-1">Kode Akun:</small>
                      <SearchableSelect
                        value={config.pendapatan?.kode_akun_pendapatan_buku?.value || ''}
                        onChange={(newVal) => updateConfigValue('pendapatan', 'kode_akun_pendapatan_buku', newVal)}
                        options={coaList.map(coa => ({
                          value: coa.KodeAkun,
                          label: `${coa.KodeAkun} - ${coa.NamaAkun} (${coa.TipeAkun})`
                        }))}
                        placeholder="-- Pilih kode akun pendapatan buku --"
                      />
                    </div>
                    
                    {/* Uang Sampul */}
                    <div className="col-md-6 mb-3">
                      <label><strong>Uang Sampul (Rp)</strong></label>
                      <input 
                        type="number" 
                        className="form-control mb-2" 
                        value={config.pendapatan?.pendapatan_uang_sampul?.value || ''} 
                        onChange={e => updateConfigValue('pendapatan', 'pendapatan_uang_sampul', e.target.value)}
                      />
                      <small className="text-muted d-block mb-1">Kode Akun:</small>
                      <SearchableSelect
                        value={config.pendapatan?.kode_akun_pendapatan_sampul?.value || ''}
                        onChange={(newVal) => updateConfigValue('pendapatan', 'kode_akun_pendapatan_sampul', newVal)}
                        options={coaList.map(coa => ({
                          value: coa.KodeAkun,
                          label: `${coa.KodeAkun} - ${coa.NamaAkun} (${coa.TipeAkun})`
                        }))}
                        placeholder="-- Pilih kode akun pendapatan sampul --"
                      />
                    </div>
                    
                    {/* Admin Anggota Baru */}
                    <div className="col-md-6 mb-3">
                      <label><strong>Admin Anggota Baru (Rp)</strong></label>
                      <input 
                        type="number" 
                        className="form-control mb-2" 
                        value={config.pendapatan?.pendapatan_admin_anggota_baru?.value || ''} 
                        onChange={e => updateConfigValue('pendapatan', 'pendapatan_admin_anggota_baru', e.target.value)}
                      />
                      <small className="text-muted d-block mb-1">Kode Akun:</small>
                      <SearchableSelect
                        value={config.pendapatan?.kode_akun_pendapatan_admin_anggota_baru?.value || ''}
                        onChange={(newVal) => updateConfigValue('pendapatan', 'kode_akun_pendapatan_admin_anggota_baru', newVal)}
                        options={coaList.map(coa => ({
                          value: coa.KodeAkun,
                          label: `${coa.KodeAkun} - ${coa.NamaAkun} (${coa.TipeAkun})`
                        }))}
                        placeholder="-- Pilih kode akun pendapatan admin --"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card mb-3 border-warning">
              <div className="card-header bg-warning">
                <h5 className="mb-0"><i className="fas fa-book mr-2"></i>Kode Akun Default (COA)</h5>
              </div>
              <div className="card-body">
                {Object.keys(config.akun || {}).length === 0 ? (
                  <div className="alert alert-light text-muted">Belum ada pengaturan kode akun.</div>
                ) : (
                  <div className="row">
                    {Object.entries(config.akun || {}).map(([key, val]) => (
                      <div className="col-md-6 mb-3" key={key}>
                        <label>{val?.desc || key}</label>
                        <SearchableSelect
                          value={val?.value || ''}
                          onChange={(newVal) => updateConfigValue('akun', key, newVal)}
                          options={coaList.map(coa => ({
                            value: coa.KodeAkun,
                            label: `${coa.KodeAkun} - ${coa.NamaAkun} (${coa.TipeAkun})`
                          }))}
                          placeholder="-- Ketik untuk mencari kode akun --"
                        />
                        <small className="text-muted">Terpilih: {val?.value || '-'}</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
}

export default Config;
